import { useId, type SelectHTMLAttributes } from 'react';

import styles from './Select.module.css';

export interface SelectProps extends Omit<
  SelectHTMLAttributes<HTMLSelectElement>,
  'id' | 'onChange' | 'value'
> {
  label: string;
  value: string;
  /** `[value, label]`, in the order they should be offered. */
  options: readonly (readonly [string, string])[];
  onChange: (value: string) => void;
  hint?: string;
}

/** A labelled `<select>`, wired like `TextField` so the two sit together. */
export function Select({ label, value, options, onChange, hint, ...rest }: SelectProps) {
  const id = useId();
  return (
    <div className={styles.field}>
      <label className={styles.label} htmlFor={id}>
        {label}
      </label>
      <select
        {...rest}
        aria-describedby={hint ? `${id}-hint` : undefined}
        className={styles.select}
        id={id}
        onChange={(event) => onChange(event.target.value)}
        value={value}
      >
        {options.map(([optionValue, optionLabel]) => (
          <option key={optionValue} value={optionValue}>
            {optionLabel}
          </option>
        ))}
      </select>
      {hint ? (
        <p className={styles.hint} id={`${id}-hint`}>
          {hint}
        </p>
      ) : null}
    </div>
  );
}
